import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  getNotificationSettings,
  changeNotificationSettings,
  NotificationSettingsResponse,
  ChangeNotificationSettingsRequest,
  NotificationCountResponse
} from '../services/api/notifications'
import {
  getCountNotSeen,
  getAllFiltered,
  getWsHistoryTypes,
  send,
  remove,
  removeAll,
  markAsViewed,
  markAsUnviewed,
  markAsViewedAll,
  markAsUnviewedAll
} from '../services/factory/notificationsFactory'
import { usersApiService } from '../services/api/users'

export interface UseNotificationCountReturn {
  count: number
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

export interface NotificationFilter {
  webSocketHistoryType: string[]
  tenantUuid?: string
}

export interface NotificationMeta {
  pageSize: number
  pageStart: number
  sortBy: string
  sortDesc: boolean
}

export interface GetAllFilteredParams {
  filter: NotificationFilter
  meta: NotificationMeta
}

export function useNotificationSettings() {
  return useQuery<NotificationSettingsResponse>({
    queryKey: ['notificationSettings'],
    queryFn: getNotificationSettings
  })
}

export interface WsHistoryTypesResponse {
  [key: string]: any // Define proper shape based on API response
}

export function useWsHistoryTypes() {
  return useQuery<WsHistoryTypesResponse>({
    queryKey: ['ws-history-types'],
    queryFn: async () => {
      const response = await getWsHistoryTypes()
      return response as WsHistoryTypesResponse
    },
    staleTime: 0,
    refetchOnWindowFocus: false, // Don't refetch on window focus (static data)
    refetchOnReconnect: false // Don't refetch on reconnect (static data)
  })
}

export function useChangeNotificationSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ChangeNotificationSettingsRequest) => changeNotificationSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationSettings'] })
    }
  })
}

export function useNotificationCount(): UseNotificationCountReturn {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['notification-count'],
    queryFn: async () => {
      const response = await getCountNotSeen()
      return response as NotificationCountResponse
    },
    staleTime: 0, // Always consider data stale to allow refetch
    retry: 2,
    refetchInterval: 30000, // Auto-refetch every 30 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnReconnect: true // Refetch when internet connection is restored
  })

  const handleRefetch = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    count: data?.count || 0,
    isLoading: isLoading || isFetching,
    error: error as Error | null,
    refetch: handleRefetch
  }
}

/**
 * Utility function to trigger notification count refresh from anywhere in the app
 * Usage: refreshNotificationCount(queryClient)
 */
export function refreshNotificationCount(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['notification-count'] })
}

/**
 * Hook to fetch filtered notification history (mutation)
 * Use this when you need to manually trigger fetching notifications with filters
 * @example
 * const { mutate, data, isPending } = useGetAllFilteredNotifications()
 * mutate({ filter: {...}, meta: {...} })
 */
export function useGetAllFilteredNotifications() {
  return useMutation({
    mutationFn: ({ filter, meta }: GetAllFilteredParams) => getAllFiltered(filter, meta)
  })
}

/**
 * Hook to fetch notification list with React Query
 * Provides auto-loading, refetch, and state management
 * @example
 * const { data, isLoading, refetch } = useNotificationsList({
 *   filter: {},
 *   meta: { pageStart: 0, pageSize: 20, sortBy: 'timeCreated', sortDesc: true }
 * })
 */
export function useNotificationsList(params: GetAllFilteredParams, enabled = true) {
  return useQuery({
    queryKey: ['notifications-list', params.filter, params.meta],
    queryFn: () => getAllFiltered(params.filter, params.meta),
    enabled, // Controls whether query runs automatically (replaces doPreload)
    staleTime: 0
  })
}

/**
 * Hook to fetch users and groups for notification composition
 * @example
 * const { data, isLoading } = useAssembledUsersGroups()
 */
export function useAssembledUsersGroups() {
  return useQuery({
    queryKey: ['assembled-users-groups'],
    queryFn: () => usersApiService.getAssembledUsersGroups(),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })
}

/**
 * Hook to send notification (mutation)
 * @example
 * const { mutate: sendNotification, isPending } = useSendNotification()
 * sendNotification({ to, subject, body, expiration })
 */
export function useSendNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      to,
      subject,
      body,
      expiration
    }: {
      to: { merged: any[] }
      subject: string
      body: string
      expiration?: Date
    }) => send(to, subject, body, expiration),
    onSuccess: () => {
      // Invalidate and refetch notifications list
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
      queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
    }
  })
}

/**
 * Hook to delete notifications (mutation)
 * @example
 * const { mutate: deleteNotification } = useDeleteNotification()
 * deleteNotification({ notificationIds, isWholeResultSelection, filter })
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      notificationIds,
      isWholeResultSelection,
      filter
    }: {
      notificationIds: number | number[]
      isWholeResultSelection?: boolean
      filter?: NotificationFilter
    }) => {
      if (isWholeResultSelection) {
        return removeAll(filter || { webSocketHistoryType: [] })
      }
      return remove(notificationIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
      queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
    }
  })
}

/**
 * Hook to mark notifications as viewed (mutation)
 * @example
 * const { mutate: markRead } = useMarkNotificationsAsViewed()
 * markRead({ notificationIds, isWholeResultSelection })
 */
export function useMarkNotificationsAsViewed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      notificationIds,
      isWholeResultSelection,
      filter
    }: {
      notificationIds?: number | number[]
      isWholeResultSelection?: boolean
      filter?: NotificationFilter
    }) => {
      if (isWholeResultSelection) {
        return markAsViewedAll(filter || { webSocketHistoryType: [] })
      }
      return markAsViewed(notificationIds!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
      queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
    }
  })
}

/**
 * Hook to mark notifications as unviewed (mutation)
 * @example
 * const { mutate: markUnread } = useMarkNotificationsAsUnviewed()
 * markUnread({ notificationIds, isWholeResultSelection })
 */
export function useMarkNotificationsAsUnviewed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      notificationIds,
      isWholeResultSelection,
      filter
    }: {
      notificationIds?: number | number[]
      isWholeResultSelection?: boolean
      filter?: NotificationFilter
    }) => {
      if (isWholeResultSelection) {
        return markAsUnviewedAll(filter || { webSocketHistoryType: [] })
      }
      return markAsUnviewed(notificationIds!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
      queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
    }
  })
}
